import { PhoneInput } from '@quadminds/ui-kit';
import { FormikProps } from 'formik';
import { motion, AnimatePresence } from 'framer-motion';
import { TFunction } from 'i18next';
import { Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaApple, FaMicrosoft } from 'react-icons/fa';
import { FcGoogle } from 'react-icons/fc';
import { CountryData } from 'react-phone-input-2';
import { Link } from 'react-router-dom';

import { fieldHasError, SignUpFormValues } from './signUpHelpers';
import google_icon from '../../assets/google_icon.svg';
import logo from '../../assets/logo_qm_flash.svg';
import { isFleetcare, pickPlatformValue } from '../../utils';
import InputBasic from '../InputBasic/InputBasic';
import InputPassword from '../InputPassword/InputPassword';
import Spinner from '../Spinner/Spinner';

type BaseSignUpFormProps = {
    formik: FormikProps<SignUpFormValues>;
    loading: boolean;
    signupSuccess: boolean;
    successAnimation: React.ReactNode;
    organizationName: string;
    onGoogleSignUp: () => void;
};

type BrandedSignUpFormProps = BaseSignUpFormProps & {
    email: string;
    isActivation: boolean;
    onMicrosoftSignUp: () => void;
    onAppleSignUp: () => void;
};

const BRANDED_INPUT_BASE_CLASS =
    'w-full px-3 py-2 rounded-md border text-white placeholder:text-gray-500 focus:outline-none focus:border-primary font-roboto disabled:bg-gray-700 disabled:border-gray-600 disabled:text-gray-500';

const BRANDED_THIRD_PARTY_BUTTON_CLASS =
    'w-full flex items-center justify-center gap-2 bg-transparent border border-gray-600 text-white hover:bg-gray-800/50 font-poppins py-2.5 rounded-lg transition-colors';

const ACTIVATION_SUBTITLE_KEY = isFleetcare ? 'signup.activation.fleetcareSubtitle' : 'signup.activation.subtitle';
const WELCOME_KEY = isFleetcare ? 'signup.fleetcareWelcome' : 'signup.mindyWelcome';

const brandedFieldClass = (hasError: boolean) =>
    `${BRANDED_INPUT_BASE_CLASS} ${hasError ? 'border-red-500 bg-gray-900' : 'bg-gray-900 border-gray-700'}`;

type SubmitButtonContentProps = {
    isSubmitting: boolean;
    loading: boolean;
    signupSuccess: boolean;
    isActivation: boolean;
    successAnimation: React.ReactNode;
    spinnerColor: string;
    spinnerSize: number;
};

const submitButtonContent = (
    {
        isSubmitting,
        loading,
        signupSuccess,
        isActivation,
        successAnimation,
        spinnerColor,
        spinnerSize,
    }: SubmitButtonContentProps,
    t: TFunction,
): React.ReactNode => {
    if (isSubmitting || loading) return <Spinner color={spinnerColor} height={spinnerSize} width={spinnerSize} />;
    if (signupSuccess) return successAnimation;
    if (isActivation) return t('signup.activation.button') as string;
    return t('signup.register') as string;
};

const BrandedFieldError: React.FC<{ message?: string }> = ({ message }) => (
    <AnimatePresence>
        {message && (
            <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-1 text-sm text-red-400"
            >
                {message}
            </motion.p>
        )}
    </AnimatePresence>
);

type BrandedSignUpFieldsProps = Pick<BrandedSignUpFormProps, 'formik' | 'email' | 'isActivation'>;

// Email, names and password fields of the branded (mindy / fleetcare / oms) sign up form
const BrandedSignUpFields: React.FC<BrandedSignUpFieldsProps> = ({ formik, email, isActivation }) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);
    const hasError = (key: keyof SignUpFormValues) => fieldHasError(formik.errors, formik.touched, key);
    const errorMessage = (key: keyof SignUpFormValues) => (hasError(key) ? t(formik.errors[key] as string) : undefined);

    return (
        <>
            {/* Email (readonly) */}
            <div>
                <label htmlFor="email" className="text-white text-sm mb-1.5 block font-poppins">
                    {t('signup.form.emailInput.label')}
                </label>
                <input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    readOnly
                    className="w-full px-3 py-2 rounded-md border bg-gray-800 border-gray-700 text-gray-400 font-roboto cursor-not-allowed"
                />
            </div>

            {/* First Name + Last Name side by side */}
            {!isActivation && (
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="firstName" className="text-white text-sm mb-1.5 block font-poppins">
                            {t('signup.form.name.label')}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            id="firstName"
                            name="firstName"
                            type="text"
                            placeholder={t('signup.form.name.placeholder')}
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={brandedFieldClass(hasError('firstName'))}
                        />
                        <BrandedFieldError message={errorMessage('firstName')} />
                    </div>

                    <div>
                        <label htmlFor="lastName" className="text-white text-sm mb-1.5 block font-poppins">
                            {t('signup.form.lastName.label')}
                            <span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            id="lastName"
                            name="lastName"
                            type="text"
                            placeholder={t('signup.form.lastName.placeholder')}
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={brandedFieldClass(hasError('lastName'))}
                        />
                        <BrandedFieldError message={errorMessage('lastName')} />
                    </div>
                </div>
            )}

            {/* Password */}
            <div>
                <label htmlFor="signupPassword" className="text-white text-sm mb-1.5 block font-poppins">
                    {t('signup.form.password.createLabel')}
                    <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                    <input
                        id="signupPassword"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('signup.form.password.placeholder')}
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={brandedFieldClass(hasError('password'))}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? (
                                <EyeOff className="h-4 w-4 text-gray-400" />
                            ) : (
                                <Eye className="h-4 w-4 text-gray-400" />
                            )}
                        </motion.button>
                    </div>
                </div>
                <BrandedFieldError message={errorMessage('password')} />
            </div>
        </>
    );
};

// Branded (mindy / fleetcare / oms) sign up form
export const BrandedSignUpForm: React.FC<BrandedSignUpFormProps> = ({
    formik,
    email,
    isActivation,
    loading,
    signupSuccess,
    successAnimation,
    organizationName,
    onGoogleSignUp,
    onMicrosoftSignUp,
    onAppleSignUp,
}) => {
    const { t } = useTranslation();

    return (
        <div className="w-full">
            {/* Title and description */}
            <div className="mb-6">
                <h2 className="text-2xl font-poppins font-semibold text-white mb-1">
                    {isActivation ? t('signup.activation.title') : t('signup.title')}
                </h2>
                <p className="text-sm text-gray-400 font-roboto">
                    {isActivation ? (
                        t(ACTIVATION_SUBTITLE_KEY, { org: organizationName })
                    ) : (
                        <>
                            {t(WELCOME_KEY)} <strong className="text-white">"{organizationName}"</strong>
                        </>
                    )}
                </p>
                <div className="mt-4 border-t border-gray-900"></div>
            </div>

            {/* Third-party Sign Up */}
            <div className="flex flex-col gap-2 mb-5">
                <button type="button" onClick={onGoogleSignUp} className={BRANDED_THIRD_PARTY_BUTTON_CLASS}>
                    <FcGoogle className="w-5 h-5" />
                    <span>{isActivation ? t('signup.activation.google') : t('signup.google')}</span>
                </button>
                <button type="button" onClick={onMicrosoftSignUp} className={BRANDED_THIRD_PARTY_BUTTON_CLASS}>
                    <FaMicrosoft className="w-5 h-5 text-blue-400" />
                    <span>{isActivation ? t('signup.activation.microsoft') : t('signup.microsoft')}</span>
                </button>
                <button type="button" onClick={onAppleSignUp} className={BRANDED_THIRD_PARTY_BUTTON_CLASS}>
                    <FaApple className="w-5 h-5" />
                    <span>{isActivation ? t('signup.activation.apple') : t('signup.apple')}</span>
                </button>
            </div>

            {/* Divider */}
            <div className="my-5 flex items-center gap-4">
                <div className="h-px flex-1 bg-gray-800" />
                <span className="text-sm text-gray-400 font-roboto">{t('signup.or')}</span>
                <div className="h-px flex-1 bg-gray-800" />
            </div>

            {/* Sign Up Form */}
            <form onSubmit={formik.handleSubmit} className="space-y-4">
                <BrandedSignUpFields formik={formik} email={email} isActivation={isActivation} />

                {/* Submit */}
                <button
                    type="submit"
                    disabled={formik.isSubmitting || !formik.isValid}
                    className="w-full bg-primary-500 hover:bg-primary-400 disabled:opacity-50 text-white font-poppins font-semibold py-3 rounded-lg transition-colors flex justify-center items-center"
                >
                    {submitButtonContent(
                        {
                            isSubmitting: formik.isSubmitting,
                            loading,
                            signupSuccess,
                            isActivation,
                            successAnimation,
                            spinnerColor: '#FFF',
                            spinnerSize: 24,
                        },
                        t,
                    )}
                </button>

                {/* Already have account */}
                <p className="text-sm text-gray-400 font-roboto text-center">
                    {t('signup.haveAccount')}{' '}
                    <Link className="text-primary-200 hover:text-primary-300 underline transition-colors" to="/login">
                        {t('signup.login')}
                    </Link>
                </p>
            </form>
        </div>
    );
};

const LEGAL_LINK_CLASS = pickPlatformValue({
    branded: 'text-primary-600',
    stork: 'text-blue-800',
    fallback: 'text-blue-500',
});

const LOGIN_LINK_CLASS = pickPlatformValue({
    branded: 'text-primary-600',
    stork: 'text-variant-800',
    fallback: 'text-blue-500',
});

// Classic (saas / flash / stork) sign up form
export const ClassicSignUpForm: React.FC<BaseSignUpFormProps> = ({
    formik,
    loading,
    signupSuccess,
    successAnimation,
    organizationName,
    onGoogleSignUp,
}) => {
    const { t } = useTranslation();
    const hasError = (key: keyof SignUpFormValues) => fieldHasError(formik.errors, formik.touched, key);

    const handlePhoneChange = (value: string) => {
        formik.setFieldValue('phone', value);
        formik.setFieldTouched('phone');
    };

    const handlePhoneBlur = (
        event: React.FocusEvent<HTMLInputElement>,
        data: Record<string, unknown> | CountryData,
    ) => {
        formik.handleBlur(event);
    };

    return (
        <motion.div
            className="flex flex-row justify-center w-full h-full max-h-[100vh] overflow-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className="flex flex-col justify-center items-center w-full 5xl:px-96 xl:px-28 lg:px-20 px-7 pt-8 3xl:pt-16">
                <img className="mb-8 lg:hidden" src={logo} alt="icon-logo" />
                <h3 className="font-roboto text-3xl 3xl:text-4xl font-semibold text-surface-high mb-2 text-center">
                    {t('signup.title')}
                </h3>
                <p className="text-base text-surface-high text-center w-full max-w-[300px] mb-3">
                    {t('signup.welcome')}{' '}
                    <strong>
                        {t('signup.team')} “{organizationName}”
                    </strong>
                    .
                </p>

                {/* Google Sign Up */}
                <motion.button
                    onClick={onGoogleSignUp}
                    className="flex justify-center items-center border-2 text-lg font-semibold p-1 3xl:p-2 mb-8 lg:mb-3 3xl:m-2 rounded-md w-full focus:outline-none focus:shadow-outline"
                    whileHover={{ scale: 1.02 }} // Subtle hover effect
                >
                    <img src={google_icon} className="mr-2" alt="google-icon" />
                    <span>{t('signup.google')}</span>
                </motion.button>

                {/* Sign Up Form */}
                <form onSubmit={formik.handleSubmit} className="w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <InputBasic
                            label={t('signup.form.name.label')}
                            name="firstName"
                            value={formik.values.firstName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={hasError('firstName')}
                            errorMessage={t(formik.errors.firstName as string)}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <InputBasic
                            label={t('signup.form.email.label')}
                            name="lastName"
                            value={formik.values.lastName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={hasError('lastName')}
                            errorMessage={t(formik.errors.lastName as string)}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <PhoneInput
                            id="phone"
                            name="phone"
                            label={t('signup.form.phone.label')}
                            value={formik.values.phone}
                            defaultCountry="ar"
                            onChange={handlePhoneChange}
                            onBlur={(event, data) => handlePhoneBlur(event, data)}
                            classNameInput="text-surface-high !w-full !rounded-md !h-[40px] 3xl:!h-[48px] !text-lg !border !border-gray-400 hover:!border-primary-600 focus:!border-primary-600"
                            classNameContainer=" mb-4"
                            error={hasError('phone')}
                            errorMessage={t(formik.errors.phone as string)}
                        />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                    >
                        <InputPassword
                            label={t('signup.form.password.label')}
                            name="password"
                            value={formik.values.password}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={hasError('password')}
                            errorMessage={t(formik.errors.password as string)}
                        />
                    </motion.div>

                    <p className="text-center">
                        {t('signup.legal.accept')}{' '}
                        <a
                            className={LEGAL_LINK_CLASS}
                            href="https://www.quadminds.com/terminos-y-condiciones-plataforma-quadminds/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('signup.legal.term')} {t('signup.legal.and')} {t('signup.legal.agreements')}
                        </a>
                    </p>

                    <div className="py-5">
                        <motion.button
                            className="w-full h-[40px] 3xl:h-[45px] bg-blue-800 disabled:bg-blue-200 rounded-md text-white flex justify-center items-center"
                            type="submit"
                            disabled={formik.isSubmitting || !formik.isValid}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            {submitButtonContent(
                                {
                                    isSubmitting: formik.isSubmitting,
                                    loading,
                                    signupSuccess,
                                    isActivation: false,
                                    successAnimation,
                                    spinnerColor: '#fff',
                                    spinnerSize: 40,
                                },
                                t,
                            )}
                        </motion.button>
                    </div>

                    <p className="text-center">
                        {t('signup.haveAccount')}{' '}
                        <Link className={LOGIN_LINK_CLASS} to="/login">
                            {t('signup.login')}
                        </Link>
                    </p>
                </form>
            </div>
        </motion.div>
    );
};
